import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import PaymentAPI from '../services/paymentApi';
import { useRef } from 'react';

const PageWrapper = styled.div`
  padding: 40px 24px;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
`;

const StatusCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const IconWrapper = styled.div`
  margin-bottom: 24px;
  
  svg {
    width: 64px;
    height: 64px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
`;

const Message = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 24px;
`;

const OrderInfo = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: left;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const Value = styled.span`
  color: #111827;
  font-size: 14px;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: #3b82f6;
    color: white;
    border: none;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const hasSyncedRef = useRef(false);

  const orderNo = searchParams.get('out_trade_no');
  const tradeNo = searchParams.get('trade_no');
  const [resolvedOrderNo, setResolvedOrderNo] = useState(orderNo || null);

  useEffect(() => {
    const n = orderNo || localStorage.getItem('last_payment_order_no');
    if (n) {
      setResolvedOrderNo(n);
      if (!hasSyncedRef.current) {
        hasSyncedRef.current = true;
        checkOrderStatus(n);
      }
    } else {
      setError('缺少订单号');
      setLoading(false);
    }
  }, [orderNo]);

  const checkOrderStatus = async (orderNoToCheck) => {
    try {
      try {
        await PaymentAPI.syncOrderStatus(orderNoToCheck);
      } catch (syncErr) {
      }
      
      const response = await PaymentAPI.getOrderStatus(orderNoToCheck);
      
      if (response.success && response.order) {
        setOrderInfo(response.order);
        
        // 如果支付成功，触发积分更新事件和VIP刷新事件
        if (response.order.status === 'paid') {
          // 延迟触发，确保后端已经处理完积分充值和VIP升级
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('pointsUpdated', {
              detail: { change: 0, newBalance: 0 } // Dashboard会重新获取最新余额
            }));
            // 触发VIP信息刷新事件
            window.dispatchEvent(new CustomEvent('vipInfoUpdated'));
          }, 1500);
          
          // 支付成功后启动倒计时自动进入控制台
          setCountdown(3);
          const timer = setInterval(() => {
            setCountdown(prev => {
              const next = prev - 1;
              if (next <= 0) {
                clearInterval(timer);
                handleGoDashboard();
                return 0;
              }
              return next;
            });
          }, 1000);
        }
      } else {
        setError('订单不存在或查询失败');
      }
    } catch (err) {
      setError('查询订单状态失败');
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoDashboard = () => {
    navigate('/app');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('pointsUpdated', {
        detail: { change: 0, newBalance: 0 }
      }));
      window.dispatchEvent(new CustomEvent('vipInfoUpdated'));
    }, 500);
  };

  if (loading) {
    return (
      <PageWrapper>
        <StatusCard>
          <IconWrapper>
            <Loader className="animate-spin" color="#3b82f6" />
          </IconWrapper>
          <Title>正在确认支付结果...</Title>
          <Message>请稍候，我们正在为您确认支付状态</Message>
        </StatusCard>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <StatusCard>
          <IconWrapper>
            <XCircle color="#ef4444" />
          </IconWrapper>
          <Title>支付确认失败</Title>
          <Message>{error}</Message>
          <ButtonGroup>
            <Button className="primary" onClick={handleGoHome}>
              返回首页
            </Button>
            <Button className="secondary" onClick={() => window.location.reload()}>
              重新确认
            </Button>
          </ButtonGroup>
        </StatusCard>
      </PageWrapper>
    );
  }

  const isPaid = orderInfo?.status === 'paid';

  return (
    <PageWrapper>
      <StatusCard>
        <IconWrapper>
          {isPaid ? (
            <CheckCircle color="#10b981" />
          ) : (
            <XCircle color="#ef4444" />
          )}
        </IconWrapper>
        
        <Title>
          {isPaid ? '支付成功！' : '支付未完成'}
        </Title>
        
        <Message>
          {isPaid 
            ? (orderInfo?.subscription_plan 
                ? '恭喜您成功订阅职唛AI Pro，现在可以享受全部会员权益了！'
                : '恭喜您充值成功，积分已到账！')
            : '您的支付尚未完成，请重新支付或联系客服'
          }
        </Message>
        
        {isPaid && countdown > 0 && (
          <Message>将在 {countdown} 秒后自动进入控制台</Message>
        )}

        {orderInfo && (
          <OrderInfo>
            <InfoRow>
              <Label>订单号：</Label>
              <Value>{orderInfo.order_no}</Value>
            </InfoRow>
            <InfoRow>
              <Label>支付金额：</Label>
              <Value>¥{orderInfo.amount}</Value>
            </InfoRow>
            <InfoRow>
              <Label>订单状态：</Label>
              <Value>
                {orderInfo.status === 'paid' ? '已支付' : 
                 orderInfo.status === 'pending' ? '待支付' : 
                 orderInfo.status === 'failed' ? '支付失败' : 
                 orderInfo.status === 'cancelled' ? '已取消' : 
                 orderInfo.status}
              </Value>
            </InfoRow>
            {tradeNo && (
              <InfoRow>
                <Label>交易号：</Label>
                <Value>{tradeNo}</Value>
              </InfoRow>
            )}
            {orderInfo.paid_at && (
              <InfoRow>
                <Label>支付时间：</Label>
                <Value>{new Date(orderInfo.paid_at).toLocaleString()}</Value>
              </InfoRow>
            )}
          </OrderInfo>
        )}

        <ButtonGroup>
          {isPaid ? (
            <>
              <Button className="primary" onClick={handleGoDashboard}>
                立即进入控制台
              </Button>
            </>
          ) : (
            <>
              <Button className="primary" onClick={() => navigate('/app/pricing')}>
                重新支付
              </Button>
              <Button className="secondary" onClick={handleGoHome}>
                返回首页
              </Button>
            </>
          )}
        </ButtonGroup>
      </StatusCard>
    </PageWrapper>
  );
};

export default PaymentSuccessPage;
